import React, { useState, useEffect, useRef, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Network, Search, Loader2 } from 'lucide-react';
import { canonAPI } from '../lib/api';
import { useTranslation } from '../hooks/useTranslation';

export default function GraphPage() {
    const { t, currentLang } = useTranslation();
    const fgRef = useRef();

    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Selection state
    const [hoverNode, setHoverNode] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const containerRef = useRef();

    // Resize handler for responsive canvas
    useEffect(() => {
        const updateDimensions = () => {
            if (!containerRef.current) return;

            const next = {
                width: containerRef.current.clientWidth,
                height: window.innerHeight - 200 // Subtract header/padding height
            };

            setDimensions(prev => (
                prev.width === next.width && prev.height === next.height ? prev : next
            ));
        };

        updateDimensions();

        const handleResize = () => {
            updateDimensions();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch the graph data on mount
    useEffect(() => {
        const fetchGraph = async () => {
            try {
                // Fetch the translated structures from localized API client
                const [articlesRes, principlesRes, glossaryRes] = await Promise.all([
                    canonAPI.getArticles(),
                    canonAPI.getPrinciples(),
                    canonAPI.getGlossary()
                ]);

                const articles = articlesRes.data;
                const principles = principlesRes.data;
                const glossary = glossaryRes.data;

                const nodes = [];
                const links = [];

                // 1. Map Articles
                articles.forEach(article => {
                    const nodeId = `article_${article.number}`;
                    nodes.push({
                        id: nodeId,
                        group: "article",
                        name: `${t('graph.articles').slice(0, -1) || "Article"} ${article.number}: ${article.title}`,
                        description: article.content.length > 100 ? article.content.substring(0, 100) + '...' : article.content
                    });
                });

                // 2. Map Principles
                principles.forEach(principle => {
                    // Use a unique static ID based on index since names are localized
                    const nodeId = `principle_${principle.name.toLowerCase().replace(/ /g, '_')}`;
                    nodes.push({
                        id: nodeId,
                        group: "principle",
                        name: principle.name,
                        description: principle.description
                    });

                    // Link Principles -> Articles using static article numbers
                    (principle.related_articles || []).forEach(artNum => {
                        links.push({
                            source: nodeId,
                            target: `article_${artNum}`,
                            value: 1
                        });
                    });
                });

                // 3. Map Glossary Terms
                glossary.forEach(term => {
                    const nodeId = `glossary_${term.term.toLowerCase().replace(/ /g, '_')}`;
                    nodes.push({
                        id: nodeId,
                        group: "glossary",
                        name: term.term,
                        description: term.definition
                    });
                    
                    if (term.related_rules && typeof term.related_rules === 'string' && term.related_rules.includes('Article')) {
                        const numbers = term.related_rules.match(/\d+/g) || [];
                        numbers.forEach(num => {
                            links.push({
                                source: nodeId,
                                target: `article_${num}`,
                                value: 0.5
                            });
                        });
                    }
                });

                setGraphData({ nodes, links });
            } catch (err) {
                console.error("Failed to load graph data locally", err);
                setError("Failed to construct localized Knowledge Graph");
            } finally {
                setLoading(false);
            }
        };
        fetchGraph();
    }, [currentLang]);

    // Visual Settings Map
    const GROUP_COLORS = {
        principle: '#9333ea', // Purple
        article: '#2563eb',   // Blue
        glossary: '#16a34a'   // Green
    };

    const getNodeColor = useCallback((node) => {
        if (selectedNode) {
            // Highlight selected node and its direct neighbors
            if (node.id === selectedNode.id) return '#facc15'; // Yellow for selected

            const isNeighbor = graphData.links.some(l =>
                (l.source.id === selectedNode.id && l.target.id === node.id) ||
                (l.target.id === selectedNode.id && l.source.id === node.id)
            );

            if (isNeighbor) return GROUP_COLORS[node.group] || '#666';
            return '#333333'; // Fade out non-connected
        }

        // Hover logic
        if (hoverNode) {
            if (node.id === hoverNode.id) return '#facc15';
            const isNeighbor = graphData.links.some(l =>
                (l.source.id === hoverNode.id && l.target.id === node.id) ||
                (l.target.id === hoverNode.id && l.source.id === node.id)
            );
            if (isNeighbor) return GROUP_COLORS[node.group] || '#666';
            return '#333333';
        }

        // Default 
        return GROUP_COLORS[node.group] || '#666';
    }, [selectedNode, hoverNode, graphData]);

    const getLinkColor = useCallback((link) => {
        if (selectedNode) {
            const isConnected = link.source.id === selectedNode.id || link.target.id === selectedNode.id;
            return isConnected ? '#555555' : 'transparent'; // Hide non-connected lines
        }
        if (hoverNode) {
            const isConnected = link.source.id === hoverNode.id || link.target.id === hoverNode.id;
            return isConnected ? '#555555' : 'rgba(255,255,255,0.05)';
        }
        return 'rgba(255,255,255,0.1)';
    }, [selectedNode, hoverNode]);

    const handleNodeClick = useCallback((node) => {
        setSelectedNode(prev => prev?.id === node.id ? null : node);

        // Auto center and zoom to node
        if (fgRef.current && node) {
            fgRef.current.centerAt(node.x, node.y, 1000);
            fgRef.current.zoom(2.5, 1000);
        }
    }, []);

    const handleNodeHover = useCallback((node) => {
        setHoverNode(prev => {
            const next = node || null;
            return prev?.id === next?.id ? prev : next;
        });
    }, []);

    // Custom node painting to draw text labels permanently
    const drawNode = useCallback((node, ctx, globalScale) => {
        const label = node.name;
        const fontSize = node.group === 'principle' ? 14 / globalScale : 10 / globalScale;

        ctx.font = `${node.group === 'principle' ? 'bold' : ''} ${fontSize}px "Inter", Sans-Serif`;
        const textWidth = ctx.measureText(label).width;
        const bckgDimensions = [textWidth, fontSize].map(n => n + fontSize * 0.4); // some padding

        ctx.fillStyle = getNodeColor(node);

        ctx.beginPath();

        // Draw dot
        const radius = node.group === 'principle' ? 6 : (node.group === 'article' ? 4 : 3);
        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
        ctx.fill();

        // Draw Label if it's a principle, or if we're zoomed in enough, or if it's selected/hovered
        if (node.group === 'principle' || globalScale > 1.5 || hoverNode?.id === node.id || selectedNode?.id === node.id) {
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            ctx.fillStyle = 'white';
            // Faded text if not selected during a selection event
            if ((selectedNode || hoverNode) && getNodeColor(node) === '#333333') {
                ctx.fillStyle = 'rgba(255,255,255,0.2)';
            }

            ctx.fillText(label, node.x, node.y + radius + (fontSize / 2) + 2);
        }
    }, [getNodeColor, hoverNode, selectedNode]);

    return (
        <div className="container mx-auto px-4 py-8" data-testid="graph-page">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
                        <Network className="h-4 w-4" />
                        <span>{t('graph.title') || "Knowledge Graph"}</span>
                    </div>
                    <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                        {t('graph.title') || "Knowledge Graph"}
                    </h1>
                    <p className="text-muted-foreground max-w-2xl text-sm">
                        {t('graph.subtitle') || "Visually explore the mathematical relationships governing the Canon. Click nodes to expand their conceptual lineage."}
                    </p>
                </div>

                {/* Legend */}
                <div className="flex gap-3 text-xs flex-wrap items-center bg-accent/5 p-3 rounded-lg border border-border/50">
                    <span className="font-semibold mr-2">{t('graph.legend') || "Legend:"}</span>
                    <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-purple-600 mr-1"></div> {t('graph.principles') || "Principles"}</div>
                    <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-blue-600 mr-1"></div> {t('graph.articles') || "Articles"}</div>
                    <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-green-600 mr-1"></div> {t('graph.glossary') || "Glossary"}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

                {/* Canvas Area */}
                <div
                    ref={containerRef}
                    className="lg:col-span-3 rounded-xl overflow-hidden border border-border shadow-sm bg-black relative"
                    style={{ height: Math.max(dimensions.height, 500) }}
                >
                    {loading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground z-10">
                            <Loader2 className="h-8 w-8 animate-spin mb-4" />
                            <p>Loading neural map...</p>
                        </div>
                    ) : error ? (
                        <div className="absolute inset-0 flex items-center justify-center text-red-400 z-10">
                            <p>{error}</p>
                        </div>
                    ) : (
                        <ForceGraph2D
                            ref={fgRef}
                            width={dimensions.width}
                            height={Math.max(dimensions.height, 500)}
                            graphData={graphData}
                            nodeLabel={() => ''} // Disable default tooltip in favor of sidebar
                            nodeRelSize={6}
                            linkColor={getLinkColor}
                            linkWidth={link => (selectedNode?.id === link.source.id || selectedNode?.id === link.target.id) ? 2 : 1}
                            nodeCanvasObject={drawNode}
                            onNodeHover={handleNodeHover}
                            onNodeClick={handleNodeClick}
                            onBackgroundClick={() => setSelectedNode(null)}
                            cooldownTicks={100} // Stop moving eventually
                            d3AlphaDecay={0.02}
                            d3VelocityDecay={0.3}
                            autoPauseRedraw={true}
                        />
                    )}

                    {/* Floating reset button */}
                    <div className="absolute top-4 right-4 z-10 flex gap-2">
                        <Badge
                            variant="secondary"
                            className="cursor-pointer bg-black/50 hover:bg-black/80 backdrop-blur"
                            onClick={() => {
                                setSelectedNode(null);
                                if (fgRef.current) {
                                    fgRef.current.zoomToFit(400);
                                }
                            }}
                        >
                            Reset View
                        </Badge>
                    </div>
                </div>

                {/* Info Sidebar */}
                <div className="lg:col-span-1 border border-border/50 rounded-xl bg-card p-0 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-border/50 bg-muted/20">
                        <h3 className="font-semibold flex items-center">
                            <Search className="h-4 w-4 mr-2 text-muted-foreground" />
                            Inspector
                        </h3>
                    </div>

                    <div className="p-5 flex-1 overflow-y-auto">
                        {selectedNode ? (
                            <div className="space-y-4 animate-in fade-in duration-200">
                                <div>
                                    <Badge
                                        variant="outline"
                                        style={{
                                            borderColor: GROUP_COLORS[selectedNode.group],
                                            color: GROUP_COLORS[selectedNode.group]
                                        }}
                                        className="mb-2 capitalize"
                                    >
                                        {selectedNode.group}
                                    </Badge>
                                    <h2 className="text-xl font-bold">{selectedNode.name}</h2>
                                </div>

                                <div className="text-sm text-foreground/80 leading-relaxed rounded-lg bg-accent/5 border border-border/30 p-4">
                                    {selectedNode.description}
                                </div>

                                {/* Connection Stats */}
                                <div className="pt-4 border-t border-border/50">
                                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Connected Nodes</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {graphData.links
                                            .filter(l => l.source.id === selectedNode.id || l.target.id === selectedNode.id)
                                            .map(l => {
                                                const neighbor = l.source.id === selectedNode.id ? l.target : l.source;
                                                // Handle initial load where target/source might just be strings instead of objects 
                                                // before internal react-force-graph mapping binds them
                                                const nName = typeof neighbor === 'object' ? neighbor.name : neighbor;
                                                const nGroup = typeof neighbor === 'object' ? neighbor.group : 'article';

                                                return (
                                                    <Badge key={typeof neighbor === 'object' ? neighbor.id : neighbor} variant="secondary" className="text-xs font-normal">
                                                        {nName}
                                                    </Badge>
                                                );
                                            })}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-8">
                                <Network className="h-10 w-10 mb-3 opacity-20" />
                                <p className="text-sm">Click any node on the canvas to inspect its definitions and connected artifacts.</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
